// src/pages/CommunityDetailPage.js
// Shared detail page for both Noticeboard items and Local Meet-ups —
// same shape (title, description, category, contact), just a different
// Firestore collection depending on the route.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { NOTICE_CATEGORIES, ACTIVITY_TYPES } from './CommunityPage';
import { format } from 'date-fns';

const fmtDate = (date) => {
  if (!date) return '';
  try {
    const d = date?.toDate ? date.toDate() : new Date(date);
    return format(d, 'd MMM yyyy');
  } catch { return ''; }
};

const CommunityDetailPage = ({ type }) => { // type: 'notice' | 'meetup'
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  const collectionName = type === 'notice' ? 'notices' : 'meetLocals';
  const categoryOptions = type === 'notice' ? NOTICE_CATEGORIES : ACTIVITY_TYPES;

  useEffect(() => {
    getDoc(doc(db, collectionName, id))
      .then(snap => { if (snap.exists()) setItem({ id: snap.id, ...snap.data() }); })
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, [id, collectionName]);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>;
  if (!item) return <div className="empty-state"><div className="empty-state-title">Not found</div></div>;

  const cat = categoryOptions.find(c => c.value === (item.category || item.activityType)) || categoryOptions[0];
  const prefs = item.contactPrefs;

  return (
    <div className="page" style={{ background: 'var(--cream)', padding: '52px 20px 32px' }}>
      <button onClick={() => navigate(-1)} style={s.backBtn}>← Back</button>

      <div style={s.typeBadge}>{cat.emoji} {cat.label}</div>
      <h1 style={s.title}>{item.title}</h1>
      <div style={s.date}>{fmtDate(item.createdAt)}</div>

      {item.description && <div style={s.desc}>{item.description}</div>}

      <div style={s.contactRow}>
        {item.phone && (!prefs || prefs.includes('call')) && (
          <a href={`tel:${item.phone}`} style={s.contactBtnSec}>📞 Call</a>
        )}
        {item.phone && (!prefs || prefs.includes('text')) && (
          <a href={`sms:${item.phone}`} style={s.contactBtnSec}>💬 Text</a>
        )}
        {item.email && (!prefs || prefs.includes('email')) && (
          <a href={`mailto:${item.email}`} style={s.contactBtn}>✉️ Email</a>
        )}
        {!item.phone && !item.email && (
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>No contact details provided.</div>
        )}
      </div>
    </div>
  );
};

const s = {
  backBtn: { background: 'none', border: 'none', color: 'var(--sage-dark)', fontSize: 14, fontWeight: 700, cursor: 'pointer', padding: 0, marginBottom: 18 },
  typeBadge: { display: 'inline-block', background: 'var(--sage-pale)', color: 'var(--sage-dark)', fontSize: 12, fontWeight: 700, borderRadius: 100, padding: '5px 12px', marginBottom: 10 },
  title: { fontSize: 24, fontWeight: 800, color: 'var(--ink)', marginBottom: 6, lineHeight: 1.25 },
  date: { fontSize: 13, color: 'var(--muted)', fontWeight: 600, marginBottom: 20 },
  desc: { fontSize: 15, color: 'var(--ink)', lineHeight: 1.6, marginBottom: 24 },
  contactRow: { display: 'flex', gap: 10 },
  contactBtn: { flex: 1, textAlign: 'center', padding: '13px 16px', borderRadius: 14, background: 'var(--sage-deep)', color: 'white', fontWeight: 700, fontSize: 14, textDecoration: 'none' },
  contactBtnSec: { flex: 1, textAlign: 'center', padding: '13px 16px', borderRadius: 14, background: 'var(--sage-pale)', color: 'var(--sage-dark)', fontWeight: 700, fontSize: 14, textDecoration: 'none' },
};

export default CommunityDetailPage;
